import _defaultTo from 'lodash/defaultTo';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _includes from 'lodash/includes';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _orderBy from 'lodash/orderBy';
import _parseInt from 'lodash/parseInt';
import _split from 'lodash/split';
// Serialization
import { deserialize, serialize } from 'serializr';
import { scenarioSchema } from './schemas';
// Color
import { installationScenarioPriorityPalette, statusPalette } from '../components/core/colors';
// Models
import mDevice from './device';
// Permissions
import permissions from '../constants/Permissions';
// Custom
import { findInList, objectsToKeys } from '../utils/Tools';

/**
* Serialization
*/
const serializerKey = 'scenario';

const deserializeScenario = (data) => deserialize(scenarioSchema, data);

const serializeScenario = ({
  name, deviceId, successCondition, selfExclusion, priority, scenarioTriggers, actions, inputCondition, inputTrigger, involvedInAntipassback,
}) => ({
  [serializerKey]: {
    ...serialize(scenarioSchema, {
      name: _defaultTo(name, null),
      device_id: _defaultTo(deviceId, null),
      success_condition: _defaultTo(successCondition, null),
      ...(!_isNil(selfExclusion) && { self_exclusion: selfExclusion }),
      priority: _defaultTo(priority, null),
      ...(scenarioTriggers && { scenario_triggers: scenarioTriggers }),
      ...(actions && { actions }),
      ...(inputCondition && { input_condition: inputCondition }),
      ...(inputTrigger && { input_trigger: inputTrigger }),
      ...(!_isNil(involvedInAntipassback) && { involved_in_antipassback: involvedInAntipassback }),
    }),
  },
});

/**
* Lists
*/
const scenarioLogStatusList = [
  {
    key: 'success',
    value: 'success',
    labelId: 'scenarioLog.status.success',
    icon: 'success',
    color: statusPalette.success,
  },
  {
    key: 'error_input_contition',
    value: 'error_input_contition',
    labelId: 'scenarioLog.status.error_input_contition',
    icon: 'warning',
    color: statusPalette.warning,
  },
  {
    key: 'error_self_exclusion',
    value: 'error_self_exclusion',
    labelId: 'scenarioLog.status.error_self_exclusion',
    icon: 'warning',
    color: statusPalette.warning,
  },
  {
    key: 'error',
    value: 'error',
    labelId: 'scenarioLog.status.error',
    icon: 'warning',
    color: statusPalette.warning,
  },
];

const scenarioTriggerTypesList = [
  {
    key: 'input',
    value: 'input',
    labelId: 'scenarioTrigger.type.input',
    icon: 'scTrigTypeInput',
  },
  {
    key: 'output',
    value: 'output',
    labelId: 'scenarioTrigger.type.output',
    icon: 'scTrigTypeOutput',
  },
  {
    key: 'na',
    value: 'na',
    labelId: 'scenarioTrigger.type.na',
    icon: 'scTrigTypeNa',
  },
];

const scenarioSuccessConditionsList = [
  {
    key: 'and',
    value: 'and',
    labelId: 'scenario.successCondition.and',
  },
  {
    key: 'or',
    value: 'or',
    labelId: 'scenario.successCondition.or',
  },
  {
    key: 'none',
    value: 'none',
    labelId: 'scenario.successCondition.none',
  },
];

const scenarioPrioritiesList = [
  {
    key: 'low',
    value: 'low',
    labelId: 'scenario.priority.low',
    icon: 'scPriority',
    color: installationScenarioPriorityPalette.low,
  },
  {
    key: 'normal',
    value: 'normal',
    labelId: 'scenario.priority.normal',
    icon: 'scPriority',
    color: installationScenarioPriorityPalette.normal,
  },
  {
    key: 'high',
    value: 'high',
    labelId: 'scenario.priority.high',
    icon: 'scPriority',
    color: installationScenarioPriorityPalette.high,
  },
];

/**
* Utils
*/
const scenarioTriggerTypesFromAccessWayTypeMap = {
  phone: ['gsm'],
  keypad_code: ['keypad'],
  badge: ['rfid_reader', 'uhf_reader'],
  license_plate: ['license_plate_reader'],
};

const sortScenarios = (scenarios, fields = ['name'], dirs = ['asc']) => (_orderBy(scenarios, fields, dirs));
const sortScenarioTriggers = (scenarioTriggers, fields = ['type'], dirs = ['asc']) => (_orderBy(scenarioTriggers, fields, dirs));
const sortScenarioActions = (actions, fields = ['delay'], dirs = ['asc']) => (_orderBy(actions, fields, dirs));

const scenarioTitle = (scenarioData) => _get(scenarioData, 'name', '');

const scenarioPriority = (scenario) => findInList(scenarioPrioritiesList, 'value', scenario, 'priority');
const scenarioTriggerType = (scenarioTrigger) => findInList(scenarioTriggerTypesList, 'value', scenarioTrigger, 'type');

const filterScenarioTriggersByAccessWayType = (scenario, devices, type) => (_filter(scenario.scenario_triggers, (trigger) => {
  let result = false;
  const deviceTrigger = mDevice.triggerFromDevices(trigger.trigger_id, devices);
  if (deviceTrigger) {
    const { equipment } = deviceTrigger;
    result = _includes(scenarioTriggerTypesFromAccessWayTypeMap[type], (equipment && equipment.type) || deviceTrigger.type);
  }
  return result;
}));

const filterScenariosByAccessWayType = (scenarios, devices, type) => (
  _filter(scenarios, (scenario) => !_isEmpty(
    filterScenarioTriggersByAccessWayType(scenario, devices, type),
  ))
);

const parseScenarioRelayAction = (relayAction, device) => {
  const idAndState = _split(relayAction, '>');
  const idRelay = _parseInt(idAndState[0]);
  const value = idAndState[1];
  const { icon, color } = _find(mDevice.relayStatesList, ['value', value]);

  return {
    slave: mDevice.slaveFromRelay(idRelay, device),
    relay: mDevice.relay(idRelay, device),
    state: {
      value, icon, color,
    },
  };
};

const parseScenarioInputCondition = (inputCondition, device) => {
  const idAndState = _split(inputCondition, '>');
  const idInput = _parseInt(idAndState[0]);
  const value = idAndState[1];
  const { icon, color } = _find(mDevice.inputStatesList, ['value', value]);

  return {
    slave: mDevice.slaveFromInput(idInput, device),
    input: mDevice.input(idInput, device),
    state: {
      value, icon, color,
    },
  };
};

const parseScenarioInputTrigger = (inputTrigger, device) => {
  const idAndState = _split(inputTrigger, '>');
  const idInput = _parseInt(idAndState[0]);
  const value = idAndState[1];
  const { icon, color } = _find(mDevice.inputMovetoStatesList, ['value', value]);

  return {
    slave: mDevice.slaveFromInput(idInput, device),
    input: mDevice.input(idInput, device),
    state: {
      value, icon, color,
    },
  };
};

const scenarioActionAndTreeToArray = (actionWithTotalDelay, actionsArray = []) => {
  let id = actionWithTotalDelay.id || 1;
  const totalDelay = actionWithTotalDelay.totalDelay || actionWithTotalDelay.delay;
  actionsArray.push({ ...actionWithTotalDelay, id, totalDelay });
  if (actionWithTotalDelay.next) {
    id = actionWithTotalDelay.next.id || (id + 1);
    scenarioActionAndTreeToArray({ ...actionWithTotalDelay.next, id, totalDelay: totalDelay + actionWithTotalDelay.next.delay }, actionsArray);
  }
  return actionsArray;
};

const getScenarioFromScenarioTriggerId = (scenarios, scenarioTriggerId) => (
  _find(scenarios, (scenario) => (_includes(objectsToKeys(scenario.scenario_triggers, 'id'), scenarioTriggerId)))
);

/**
* Model
*/
const scenario = {
  // Name
  name: 'scenario',

  // Serialization
  deserialize: deserializeScenario,
  serialize: serializeScenario,

  // Lists
  logStatusList: scenarioLogStatusList,
  triggerTypesList: scenarioTriggerTypesList,
  successConditionsList: scenarioSuccessConditionsList,
  prioritiesList: scenarioPrioritiesList,

  // Sort elements
  sortElements: sortScenarios,
  sortTriggers: sortScenarioTriggers,
  sortActions: sortScenarioActions,

  // Utils
  title: scenarioTitle,
  triggerTypesFromAccessWayTypeMap: scenarioTriggerTypesFromAccessWayTypeMap,
  priority: scenarioPriority,
  triggerType: scenarioTriggerType,
  filterTriggersByAccessWayType: filterScenarioTriggersByAccessWayType,
  filterByAccessWayType: filterScenariosByAccessWayType,
  parseRelayAction: parseScenarioRelayAction,
  parseInputCondition: parseScenarioInputCondition,
  parseInputTrigger: parseScenarioInputTrigger,
  actionAndTreeToArray: scenarioActionAndTreeToArray,
  getFromScenarioTriggerId: getScenarioFromScenarioTriggerId,

  // List features
  listFeatures: {
    // Default list param (not used)
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [],
    },

    // Columns
    columnsQuantity: (hasPermission) => ({
      xs: 2,
      sm: 5 + (hasPermission({ needed: permissions.pInstallation.scenarioTrigger, permissionsOrContext: 'installationPermissions' }) | 0),
      md: 5 + (hasPermission({ needed: permissions.pInstallation.scenarioTrigger, permissionsOrContext: 'installationPermissions' }) | 0),
      lg: 5 + (hasPermission({ needed: permissions.pInstallation.scenarioTrigger, permissionsOrContext: 'installationPermissions' }) | 0),
      xl: 5 + (hasPermission({ needed: permissions.pInstallation.scenarioTrigger, permissionsOrContext: 'installationPermissions' }) | 0),
    }),
    columns: [
      {
        key: 'name',
        labelId: 'field.name',
        field: 'name',
        sortable: false,
      },
      {
        key: 'device',
        labelId: 'field.device',
        field: 'device',
        sortable: false,
        className: 'hidden-xs-down',
      },
      {
        key: 'antipassback',
        labelId: 'field.antipassback',
        field: 'antipassback',
        sortable: false,
        className: 'table-content-1 table-content-center hidden-xs-down',
      },
      {
        key: 'priority',
        labelId: 'field.priority',
        field: 'priority',
        sortable: false,
        className: 'table-content-1 table-content-center hidden-xs-down',
      },
      {
        key: 'status',
        labelId: 'field.status',
        field: 'status',
        sortable: false,
        className: 'table-content-1 table-content-center',
      },
      {
        key: 'trigger',
        permissions: {
          needed: permissions.pInstallation.scenarioTrigger,
          permissionsOrContext: 'installationPermissions',
        },
        labelId: 'field.triggerAction',
        field: 'trigger',
        sortable: false,
        className: 'table-content-1 hidden-xs-down',
      },
    ],

    // Search fields
    searchFields: [],
  },
};

module.exports = scenario;



// WEBPACK FOOTER //
// ./models/scenario.js